import React, { useState, useEffect } from 'react';
import { getProducts, getCategories, getBrands, createProduct, updateProduct, deleteProduct, uploadImage } from '../../lib/api';
import { slugify } from '../../lib/utils';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, Image, Upload, X } from 'lucide-react';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category_id: '',
    brand_id: '',
    product_type: '',
    volume_ml: '',
    availability: 'In Stock',
    moq: '',
    short_description: '',
    full_description: '',
    notes_top: '',
    notes_middle: '',
    notes_base: '',
    images: [],
    featured: false,
  });
  const [imageInput, setImageInput] = useState('');

  const fetchData = async () => {
    try {
      const [prodRes, catsRes, brandsRes] = await Promise.all([
        getProducts({ limit: 500 }),
        getCategories(),
        getBrands(),
      ]);
      setProducts(prodRes.data);
      setCategories(catsRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({ ...formData, name, slug: slugify(name) });
  };

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setFormData({ ...formData, images: [...formData.images, imageInput.trim()] });
      setImageInput('');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setFormData({ ...formData, images: [...formData.images, res.data.url] });
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id || !formData.brand_id) {
      toast.error('Please select category and brand');
      return;
    }
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      category_id: product.category_id,
      brand_id: product.brand_id,
      product_type: product.product_type || '',
      volume_ml: product.volume_ml || '',
      availability: product.availability || 'In Stock',
      moq: product.moq || '',
      short_description: product.short_description || '',
      full_description: product.full_description || '',
      notes_top: product.notes_top || '',
      notes_middle: product.notes_middle || '',
      notes_base: product.notes_base || '',
      images: product.images || [],
      featured: product.featured || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(id);
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      slug: '',
      category_id: '',
      brand_id: '',
      product_type: '',
      volume_ml: '',
      availability: 'In Stock',
      moq: '',
      short_description: '',
      full_description: '',
      notes_top: '',
      notes_middle: '',
      notes_base: '',
      images: [],
      featured: false,
    });
    setImageInput('');
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '-';

  const productTypes = ['EDP', 'EDT', 'Parfum', 'Deo', 'Body Mist', 'Attar', 'Gift Set', 'Other'];

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-serif text-[#0B1F3B]">Products</h1>
          <p className="text-gray-500">Manage product catalog ({products.length} products)</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      <div className="bg-white border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : products.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">Image</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-left">Brand</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Volume</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td className="px-4 py-3">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 flex items-center justify-center rounded">
                          <Image className="h-4 w-4 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{product.name}</div>
                      {product.featured && (
                        <span className="text-xs text-[#D4AF37]">Featured</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getCategoryName(product.category_id)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{getBrandName(product.brand_id)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{product.product_type || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{product.volume_ml || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        product.availability === 'In Stock' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {product.availability}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleEdit(product)} className="p-2 text-gray-400 hover:text-[#0B1F3B]">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(product.id)} className="p-2 text-gray-400 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <p>No products yet. Click "Add Product" to create one.</p>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={formData.name} onChange={handleNameChange} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} />
              </div>
            </div>

            {/* Category & Brand */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category_id} onValueChange={(val) => setFormData({ ...formData, category_id: val })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Brand *</Label>
                <Select value={formData.brand_id} onValueChange={(val) => setFormData({ ...formData, brand_id: val })}>
                  <SelectTrigger><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Type, Volume, Availability */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Product Type</Label>
                <Select value={formData.product_type} onValueChange={(val) => setFormData({ ...formData, product_type: val })}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {productTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="volume_ml">Volume</Label>
                <Input id="volume_ml" value={formData.volume_ml} onChange={(e) => setFormData({ ...formData, volume_ml: e.target.value })} placeholder="e.g., 100ml" />
              </div>
              <div className="space-y-2">
                <Label>Availability</Label>
                <Select value={formData.availability} onValueChange={(val) => setFormData({ ...formData, availability: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Stock">In Stock</SelectItem>
                    <SelectItem value="On Request">On Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* MOQ */}
            <div className="space-y-2">
              <Label htmlFor="moq">Minimum Order Quantity (MOQ)</Label>
              <Input id="moq" value={formData.moq} onChange={(e) => setFormData({ ...formData, moq: e.target.value })} placeholder="e.g., 6 units" />
            </div>

            {/* Descriptions */}
            <div className="space-y-2">
              <Label htmlFor="short_description">Short Description</Label>
              <Textarea id="short_description" value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} rows={2} placeholder="Brief product summary..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="full_description">Full Description</Label>
              <Textarea id="full_description" value={formData.full_description} onChange={(e) => setFormData({ ...formData, full_description: e.target.value })} rows={3} placeholder="Detailed product description..." />
            </div>

            {/* Fragrance Notes */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium">Fragrance Notes</Label>
              <div className="grid grid-cols-3 gap-4 mt-2">
                <div className="space-y-2">
                  <Label htmlFor="notes_top" className="text-sm text-gray-500">Top Notes</Label>
                  <Input id="notes_top" value={formData.notes_top} onChange={(e) => setFormData({ ...formData, notes_top: e.target.value })} placeholder="Bergamot, Lemon..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes_middle" className="text-sm text-gray-500">Middle Notes</Label>
                  <Input id="notes_middle" value={formData.notes_middle} onChange={(e) => setFormData({ ...formData, notes_middle: e.target.value })} placeholder="Rose, Jasmine..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes_base" className="text-sm text-gray-500">Base Notes</Label>
                  <Input id="notes_base" value={formData.notes_base} onChange={(e) => setFormData({ ...formData, notes_base: e.target.value })} placeholder="Musk, Amber..." />
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-2 border-t pt-4">
              <Label>Product Images</Label>
              <div className="flex gap-2">
                <Input
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  placeholder="Paste image URL"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddImage}>Add URL</Button>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span><Upload className="h-4 w-4 mr-1" />{uploading ? '...' : 'Upload'}</span>
                  </Button>
                </label>
              </div>
              {formData.images.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-20 h-20 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(i)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Featured */}
            <div className="flex items-center gap-2 border-t pt-4">
              <Checkbox
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
              <Label htmlFor="featured" className="cursor-pointer">Featured Product (shown on homepage)</Label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1">{editingProduct ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
